import type {
    ReactNode
} from "react";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    DocsTocItem
} from "../../components/toc";
import {
    PageHeader,
    InlineCode,
    Paragraph,
    DocsTable,
    Article,
    Section,
    Cards
} from "../../components/content";
import {
    ANATOMY_SAMPLES
} from "../../anatomy/samples";
import {
    DOCS_PAGES
} from "../../nav";
import Anatomy from "../../components/anatomy";

export type PropRow = {
    defaultValue?: string;
    description: string;
    type: string;
    name: string;
};

const findComponentPage = (name: string) => DOCS_PAGES.find((page) => page.title === name && page.path.startsWith("/components/"));

const SAMPLE_IMAGE = [
    "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"640\" height=\"360\" viewBox=\"0 0 640 360\">",
    "<defs><linearGradient id=\"sky\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">",
    "<stop offset=\"0\" stop-color=\"#5b7cfa\"/><stop offset=\"0.65\" stop-color=\"#f6a38a\"/><stop offset=\"1\" stop-color=\"#fbd38d\"/>",
    "</linearGradient></defs>",
    "<rect width=\"640\" height=\"360\" fill=\"url(#sky)\"/>",
    "<circle cx=\"440\" cy=\"190\" r=\"54\" fill=\"#fff7e0\" fill-opacity=\"0.9\"/>",
    "<path d=\"M0 360V250l120-90 100 80 90-60 130 110 80-50 120 90v30z\" fill=\"#394867\"/>",
    "<path d=\"M0 360V300l140-60 120 50 110-40 270 70v40z\" fill=\"#222c40\"/>",
    "</svg>"
].join("");

export const SAMPLE_PHOTO_SOURCE = {
    uri: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SAMPLE_IMAGE)}`
};

export const hasAnatomySample = (name: string) => Boolean(ANATOMY_SAMPLES[name]);

export const ComponentHeader = ({
    aside,
    title,
    lead,
    name
}: {
    aside?: string;
    title?: string;
    lead: string;
    name: string;
}) => {
    const {
        t
    } = useDocsI18n();

    const page = findComponentPage(name);

    return <PageHeader
        breadcrumbs={[
            {
                label: t("breadcrumb.docs"),
                href: "/"
            },
            {
                label: t("breadcrumb.components")
            },
            {
                label: t(page?.groupKey ?? "")
            }
        ]}
        title={title ?? name}
        aside={aside}
        lead={lead}
    />;
};

export const AnatomySection = ({
    children,
    name
}: {
    children?: ReactNode;
    name: string;
}) => {
    const {
        t
    } = useDocsI18n();

    const sample = ANATOMY_SAMPLES[name]?.(t);

    if(!sample) {
        return null;
    }

    return <Section
        title={t("section.anatomy")}
        id="anatomy"
    >
        <Paragraph>
            {t("component.anatomyText")}
        </Paragraph>
        {children}
        <Anatomy
            maxScale={sample.maxScale}
            states={sample.states}
            names={sample.names}
            notes={sample.notes}
            width={sample.width}
            name={name}
            key={name}
        >
            {sample.node}
        </Anatomy>
    </Section>;
};

export const ExampleSection = ({
    name
}: {
    name: string;
}) => {
    const {
        t
    } = useDocsI18n();

    const route = findComponentPage(name)?.exampleRoute ?? "";

    return <Section
        title={t("section.example")}
        id="example"
    >
        <Paragraph
            isMuted={true}
        >
            {t("component.example")}
        </Paragraph>
        <Cards
            items={[
                {
                    href: `http://localhost:3000/components/${route}`,
                    text: `localhost:3000/components/${route}`,
                    title: t("component.openExample", [
                        name
                    ]),
                    isExternal: true
                }
            ]}
        />
    </Section>;
};

export const PropsTable = ({
    rows
}: {
    rows: Array<PropRow>;
}) => {
    const {
        t
    } = useDocsI18n();

    const code = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    return <DocsTable
        rows={rows.map((row) => [
            code(row.name),
            code(row.type),
            row.defaultValue === undefined ? "—" : code(row.defaultValue),
            row.description
        ])}
        headers={[
            t("props.name"),
            t("props.type"),
            t("props.default"),
            t("props.description")
        ]}
    />;
};

export const getSectionToc = (sections: Array<{
    title: string;
    id: string;
}>, name: string, t: DocsTranslate): Array<DocsTocItem> => {
    const items = [...sections];

    const overviewIndex = items.findIndex((item) => item.id === "overview");

    if(hasAnatomySample(name)) {
        items.splice(overviewIndex + 1, 0, {
            title: t("section.anatomy"),
            id: "anatomy"
        });
    }

    items.push({
        title: t("section.example"),
        id: "example"
    });

    return items;
};

export const getPendingToc = (name: string, t: DocsTranslate) => getSectionToc([], name, t);

export const PendingComponentPage = ({
    name
}: {
    name: string;
}) => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ComponentHeader
            lead={t("component.lead", [
                name
            ])}
            name={name}
        />
        <AnatomySection
            name={name}
        />
        <ExampleSection
            name={name}
        />
    </Article>;
};
